/**
 * Which media path a viewer is pointed at, which is not always the publisher's.
 *
 * Stage 2 of `docs/plans/2026-08-30-picture-and-sound-as-two-decisions.md`. A
 * feed whose picture cleared the standard and whose sound did not goes to air
 * SILENT, and silent has to mean the audio is not delivered rather than not
 * played. `mediamtx.yml` serves the publisher's own path straight through, so
 * there is nowhere in that chain for an editorial decision to take effect: the
 * viewer is pointed at a different path instead, fed by a copy remux that
 * carries the video track and drops the audio.
 *
 * **Muting the player is not an option and the reason is the whole product.**
 * The gate refused that audio. Delivering it with `<video muted>` ships it to
 * every viewer and leaves the unmute button one click away, which is a refusal
 * that can be overridden by the person it was protecting somebody from.
 *
 * Pure and shared, so the server that spawns the remux and the client that
 * plays the result cannot disagree about the name. Two files deriving one
 * string is the second-copy failure this repository has paid for repeatedly;
 * the difference here is that a disagreement would put the refused audio to
 * air, because the client would fall back to the path that has it.
 */

/**
 * The suffix, separate so nothing constructs the name by concatenation.
 *
 * MediaMTX path names allow this character set, and the suffix is deliberately
 * not a slash: a nested path would be a different path TREE, and the recording
 * and auth rules are written per top-level path.
 */
export const MUTED_SUFFIX = '-muted';

/**
 * Joins a picture path to a DIFFERENT feed's sound path. Stage 4.
 *
 * Both names are in it deliberately. A derived name like `<picture>-mix` would
 * not change when the sound source changed, so a viewer already playing it would
 * keep pulling the old pairing's segments: the player rebuilds on a path change
 * and on nothing else.
 */
export const MIX_INFIX = '-with-';

/**
 * Where the viewer's player should point, given what the switcher decided.
 *
 * Three cases, and the first is every ordinary broadcast:
 *
 * - `sound === picture`: the publisher's own path, untouched, no process.
 * - `sound === null`: the muted sibling, video only.
 * - anything else: the mix, this picture with that sound.
 */
export function airPath(picture: string, sound: string | null): string {
  if (sound === picture) return picture;
  if (sound === null) return `${picture}${MUTED_SUFFIX}`;
  return `${picture}${MIX_INFIX}${sound}`;
}

/** Is this a path the switchboard produces rather than one a publisher owns? */
export function isDerivedPath(path: string): boolean {
  return path.endsWith(MUTED_SUFFIX) || path.includes(MIX_INFIX);
}

/**
 * Is this the path a publisher publishes to, rather than one we derived?
 *
 * Used by the media plane's auth callback: a muted path is readable exactly
 * when its SOURCE is on air, and nothing may publish to one directly. Without
 * this, a publisher could take the muted name and serve arbitrary audio from
 * the one path the switcher promises has none.
 */
export function isMutedPath(path: string): boolean {
  return isDerivedPath(path);
}
